import { useCallback } from 'react';
import type { ComponentProps } from 'react';

import type { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { expVal } from '@confluence/feature-experiments';

import { LightNodeSerializer } from '../light/LightNodeSerializer.tsx';
import type { AdfStats } from '../useAdfStats.ts';

import { useOnlySerializeFirstBatch } from './useOnlySerializeFirstBatch.ts';

type UseCreateSerializerProps = {
	skip?: boolean;
	totalNodeCount: AdfStats['totalNodeCount'];
	totalNodesHiddenFromView: AdfStats['totalNodesHiddenFromView'];
	adfContentId?: string;
	// Raw eligibility: normal View Page, after SSR/hydration.
	allowLightRendering?: boolean;
	// Printing bypasses LightNodeSerializer.
	forceFullSerialization?: boolean;
	onContentComplete?: () => void;
};

/**
 * Unless progressive rendering is skipped:
 *
 * | Visible nodes | SSR                    | Client                              |
 * | ------------- | ---------------------- | ----------------------------------- |
 * | < 1,750       | Render all content     | Hydrate all content                 |
 * | >= 1,750      | First 2,160px batch    | Hydrate first batch; batch the rest |
 *
 * Nodes hidden in collapsed expands are not counted because the reader cannot see them.
 * 1,750 approximates the observed p90 of 1,751 nodes, keeping roughly the smaller
 * 90% of pages on full SSR and hydration while batching larger pages.
 * 2,160px covers 99% of recorded screen heights across 18.1 million production
 * Confluence initial View Page loads in fe_performance, 19–25 September 2026 UTC.
 * Each batch targets that height with no buffer; complete top-level blocks may exceed it.
 */
const FULL_SSR_NODE_THRESHOLD = 1750;

type CreateSerializer = ComponentProps<typeof ADFRenderer>['createSerializer'];

export const useCreateSerializer = ({
	adfContentId,
	onContentComplete,
	skip,
	totalNodeCount,
	totalNodesHiddenFromView,
	allowLightRendering,
	forceFullSerialization,
}: UseCreateSerializerProps): CreateSerializer => {
	// Initially, only serialize the first batch. This is a performance optimization to delay
	// serialization of the remaining batches until later. The optimization is disabled if there
	// are not enough batches to warrant it.
	//
	// For SSR, subsequent serializations never happen.
	// For SPA, subsequent serializations happen after the first batch is rendered.
	const { onlySerializeFirstBatch, onBatchRendered } = useOnlySerializeFirstBatch({
		adfContentId,
		onContentComplete,
	});

	const createComplexPageSerializer: NonNullable<CreateSerializer> = useCallback(
		(init) => {
			const enableLightRendering = Boolean(
				allowLightRendering &&
				!onlySerializeFirstBatch &&
				!(
					typeof init.allowHeadingAnchorLinks === 'object' &&
					init.allowHeadingAnchorLinks.activeHeadingId
				) &&
				expVal('cc_enable_light_mode_progressive_rendering', 'isEnabled', false),
			);
			return new LightNodeSerializer(init, onlySerializeFirstBatch, onBatchRendered, {
				allowLightRendering: enableLightRendering,
			});
		},
		[onlySerializeFirstBatch, onBatchRendered, allowLightRendering],
	);

	if (
		!skip &&
		!forceFullSerialization &&
		totalNodeCount - totalNodesHiddenFromView >= FULL_SSR_NODE_THRESHOLD
	) {
		return createComplexPageSerializer;
	}
};
